import React from "react";
import { cn } from "../../lib/utils";
import { Input } from "./input";
import { Label } from "./label";
import { Textarea } from "./textarea";

type FieldLabelProps = {
  htmlFor: string;
  compact?: boolean;
  children: React.ReactNode;
};

export const FieldLabel: React.FC<FieldLabelProps> = ({
  htmlFor,
  compact = false,
  children,
}) => (
  <Label
    htmlFor={htmlFor}
    className={compact ? "ds-form-label-compact" : "ds-form-label"}
  >
    {children}
  </Label>
);

type InputClassProps = {
  compact?: boolean;
  className?: string;
};

const resolveInputClassName = ({
  compact = false,
  className,
}: InputClassProps) => {
  const baseClassName = compact ? "ds-input-compact" : "ds-input";
  return cn(baseClassName, className);
};

type TextInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "className"
> &
  InputClassProps;

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  ({ compact = false, className, ...props }, ref) => (
    <Input
      ref={ref}
      {...props}
      className={resolveInputClassName({ compact, className })}
    />
  ),
);
TextInput.displayName = "TextInput";

type TextAreaInputProps = Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  "className"
> &
  InputClassProps;

export const TextAreaInput = React.forwardRef<
  HTMLTextAreaElement,
  TextAreaInputProps
>(({ compact = false, className, ...props }, ref) => (
  <Textarea
    ref={ref}
    {...props}
    className={resolveInputClassName({ compact, className })}
  />
));
TextAreaInput.displayName = "TextAreaInput";

type SelectInputProps = Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  "className"
> &
  InputClassProps;

export const SelectInput = React.forwardRef<
  HTMLSelectElement,
  SelectInputProps
>(({ compact = false, className, ...props }, ref) => (
  <select
    ref={ref}
    {...props}
    className={resolveInputClassName({ compact, className })}
  />
));
SelectInput.displayName = "SelectInput";
